import React, { useEffect, useState } from "react";
import { Button, Tabs } from "@/components/ui";
import { ChevronLeft, ExternalLink, Mail, Video } from "lucide-react";
import { SocialNetworksId } from "@/interfaces";
import { helpGuideCopy, ZillowHelpVariant, zillowHelpGuideCopy } from "./shared";
import { useSocialCoachSession } from "@/utils";
import { cn } from "@/lib/utils";

interface HelpGuideProps {
  networkId: SocialNetworksId;
  onBack: () => void;
  zillowVariant?: ZillowHelpVariant;
  embedded?: boolean;
}

export function SocialHelpGuide({ networkId, onBack, zillowVariant = "lender", embedded = false }: HelpGuideProps) {
  const [selectedZillowVariant, setSelectedZillowVariant] = useState<ZillowHelpVariant>(zillowVariant);
  const { supportEmail } = useSocialCoachSession();

  useEffect(() => {
    setSelectedZillowVariant(zillowVariant);
  }, [zillowVariant]);

  const guide = networkId === "ZL" ? zillowHelpGuideCopy[selectedZillowVariant] : helpGuideCopy[networkId];

  if (!guide) return null;

  return (
    <div
      className={cn(
        "flex flex-col",
        embedded
          ? "gap-0"
          : "h-full bg-slate-50/50 rounded-2xl border border-slate-200 p-4 animate-in fade-in slide-in-from-bottom-2 duration-200"
      )}
    >
      <div className="flex items-center gap-1 mb-2 pb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={cn("text-slate-500 hover:text-slate-900 transition-colors", !embedded && "-ml-2")}
        >
          <ChevronLeft size={16} className="mr-1" />
          Back
        </Button>
      </div>

      {networkId === "ZL" && (
        <Tabs
          value={selectedZillowVariant}
          onValueChange={value => setSelectedZillowVariant(value as ZillowHelpVariant)}
          className="mb-3"
        >
          <Tabs.List className="w-full">
            <Tabs.Trigger value="lender" className="flex-1">
              Lender
            </Tabs.Trigger>
            <Tabs.Trigger value="agent" className="flex-1">
              Agent
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs>
      )}

      <div className="font-bold text-sm text-slate-800">{guide.title}</div>
      <div className="text-sm text-slate-500 mb-4">{guide.description}</div>

      <div className="flex flex-col gap-3 grow overflow-y-auto pr-1 custom-scrollbar">
        {guide.steps.map((step: any, idx: number) => {
          const Icon = step.icon;
          return (
            <div key={`${selectedZillowVariant}-${idx}`} className="flex items-start gap-3">
              <div className="bg-white p-1.5 rounded-md border border-slate-200 shadow-sm shrink-0">
                <Icon size={14} className="text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-700">{step.title}</span>
                <span className="text-sm text-slate-500 leading-tight mb-0.5">{step.description}</span>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={step.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-primary hover:underline flex items-center gap-1 w-fit"
                  >
                    {step.linkText} <ExternalLink size={12} />
                  </a>

                  {step.videoUrl && (
                    <a
                      href={step.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-slate-600 bg-white hover:bg-slate-100 hover:text-slate-900 border border-slate-200 px-2 py-1 rounded flex items-center gap-1.5 transition-colors shadow-sm w-fit"
                    >
                      <Video size={12} className="text-slate-400" />
                      {step.videoText || "Watch tutorial"}
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between items-center text-sm">
        <span className="text-slate-400">Still stuck?</span>
        <a href={`mailto:${supportEmail}`} className="flex items-center gap-1 text-slate-500 hover:text-primary">
          <Mail size={12} /> {supportEmail}
        </a>
      </div>
    </div>
  );
}
